import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { z } from "zod";
import { toast } from "sonner";
import { BellOffIcon, PhoneIcon, XIcon } from "lucide-react";
import {
  FermentationAlertStateEnum,
  type FermentationGroupAlertId,
  type FermentationGroupId,
  type OperatorId,
} from "@brewbase/db/types";
import {
  ALERT_KIND_LABELS,
  formatDuration,
  VOICE_ESCALATION_S,
} from "@brewbase/lib";
import { trpc } from "@/hooks/trpc";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Combobox } from "@/components/ui/combobox";
import { SimpleSelect } from "@/components/ui/simple-select";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { AlertRulesEditor } from "@/features/fermentation-alerts/alert-rules-editor";

export const Route = createFileRoute("/fermentations/$group_id/planning/alerts")(
  {
    component: GroupAlertsPage,
    params: z.object({ group_id: z.coerce.number() }),
  },
);

const SNOOZE_CHOICES = [
  { value: 30, label: "30 minutes" },
  { value: 60, label: "1 hour" },
  { value: 120, label: "2 hours" },
  { value: 480, label: "8 hours" },
];

/**
 * A run's own alerting: what is watched, who is called, and what is silenced.
 *
 * Everything on this page is live. The rules were copied from the procedure
 * when the group started and are the group's own from that moment, so editing
 * them changes what is being watched on this run and nothing else.
 */
function GroupAlertsPage() {
  const groupId = Route.useParams().group_id as FermentationGroupId;
  const utils = trpc.useUtils();

  const { data: rules, isLoading } =
    trpc.fermentationAlerts.listGroupRules.useQuery(groupId);
  const { data: onCall } = trpc.fermentationAlerts.listOnCall.useQuery(groupId);
  const { data: operators } = trpc.operator.list.useQuery();
  const { data: snoozes } = trpc.fermentationAlerts.listSnoozes.useQuery(groupId);
  const { data: history } = trpc.fermentationAlerts.listForGroup.useQuery({
    groupId,
    limit: 50,
  });

  const onError = (error: { message: string }) => toast.error(error.message);
  const invalidateRules = () =>
    utils.fermentationAlerts.listGroupRules.invalidate(groupId);
  const invalidateRoster = () =>
    utils.fermentationAlerts.listOnCall.invalidate(groupId);
  const invalidateSnoozes = () =>
    utils.fermentationAlerts.listSnoozes.invalidate(groupId);
  const invalidateHistory = () =>
    utils.fermentationAlerts.listForGroup.invalidate();

  const create = trpc.fermentationAlerts.createGroupRule.useMutation({
    onSuccess: invalidateRules,
    onError,
  });
  const update = trpc.fermentationAlerts.updateGroupRule.useMutation({
    onSuccess: invalidateRules,
    onError,
  });
  const remove = trpc.fermentationAlerts.deleteGroupRule.useMutation({
    onSuccess: invalidateRules,
    onError,
  });
  const adopt = trpc.fermentationAlerts.adoptProcedureRules.useMutation({
    onSuccess: (result) => {
      toast.success(
        result.copied > 0
          ? `Copied ${result.copied} alert${result.copied === 1 ? "" : "s"} from the procedure.`
          : "The procedure has no alerts to copy.",
      );
      void invalidateRules();
    },
    onError,
  });
  const addOnCall = trpc.fermentationAlerts.addOnCall.useMutation({
    onSuccess: invalidateRoster,
    onError,
  });
  const removeOnCall = trpc.fermentationAlerts.removeOnCall.useMutation({
    onSuccess: invalidateRoster,
    onError,
  });
  const snooze = trpc.fermentationAlerts.snooze.useMutation({
    onSuccess: invalidateSnoozes,
    onError,
  });
  const cancelSnooze = trpc.fermentationAlerts.cancelSnooze.useMutation({
    onSuccess: invalidateSnoozes,
    onError,
  });
  const acknowledge = trpc.fermentationAlerts.acknowledge.useMutation({
    onSuccess: invalidateHistory,
    onError,
  });
  const close = trpc.fermentationAlerts.close.useMutation({
    onSuccess: invalidateHistory,
    onError,
  });

  const rosterIds = useMemo(
    () => new Set((onCall ?? []).map((r) => r.operator_id as number)),
    [onCall],
  );
  const candidates = useMemo(
    () =>
      (operators ?? [])
        .filter((o) => o.active && !rosterIds.has(o.id as number))
        .map((o) => ({
          value: String(o.id),
          label: o.full_name ?? o.email ?? `#${o.id}`,
        })),
    [operators, rosterIds],
  );

  const [snoozeMinutes, setSnoozeMinutes] = useState(60);

  const open = (history ?? []).filter(
    (a) => a.state !== FermentationAlertStateEnum.CLEARED,
  );

  if (isLoading || !rules) {
    return (
      <div className="p-4 space-y-2">
        <Skeleton className="h-8 w-full" />
        <Skeleton className="h-8 w-full" />
      </div>
    );
  }

  return (
    <div className="p-4 space-y-8 overflow-y-auto">
      {open.length > 0 && (
        <section className="space-y-2">
          <h2 className="text-base font-semibold">Open now</h2>
          <Table>
            <TableBody>
              {open.map((alert) => (
                <TableRow key={alert.id}>
                  <TableCell className="w-36">
                    <Badge variant="destructive">
                      {ALERT_KIND_LABELS[alert.kind]}
                    </Badge>
                  </TableCell>
                  <TableCell className="w-28 font-mono text-sm">
                    {alert.fermentation_name ?? "—"}
                  </TableCell>
                  <TableCell>{alert.detail}</TableCell>
                  <TableCell className="w-48 text-sm text-muted-foreground">
                    {alert.state === FermentationAlertStateEnum.ACKED
                      ? `Acknowledged by ${alert.acked_by_name ?? "someone"}`
                      : `Raised ${new Date(alert.raised_at).toLocaleTimeString()}`}
                  </TableCell>
                  <TableCell className="w-44">
                    <div className="flex gap-1 justify-end">
                      {alert.state === FermentationAlertStateEnum.RAISED && (
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => acknowledge.mutate(alert.id)}
                        >
                          Acknowledge
                        </Button>
                      )}
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => close.mutate(alert.id)}
                      >
                        Close
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </section>
      )}

      <section className="space-y-2">
        <div className="flex items-start justify-between gap-4">
          <div>
            <h2 className="text-base font-semibold">Alerts</h2>
            <p className="text-sm text-muted-foreground max-w-3xl">
              Watched only while a tank is running and inoculated. These rules
              belong to this run — the procedure's copy is a template and
              changing it here changes nothing anywhere else.
            </p>
          </div>
          {rules.length === 0 && (
            <Button
              variant="ghost"
              size="sm"
              disabled={adopt.isPending}
              onClick={() => adopt.mutate(groupId)}
            >
              Copy from procedure
            </Button>
          )}
        </div>

        <AlertRulesEditor
          rules={rules}
          onCreate={(rule) => create.mutateAsync({ groupId, rule })}
          onUpdate={(id, rule) =>
            update.mutateAsync({ id: id as FermentationGroupAlertId, rule })
          }
          onDelete={(id) => remove.mutate(id as FermentationGroupAlertId)}
        />
      </section>

      <section className="space-y-2">
        <div>
          <h2 className="text-base font-semibold">On call</h2>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Everyone here is texted the moment an alert is raised, and called if
            nobody has acknowledged it {formatDuration(VOICE_ESCALATION_S)}{" "}
            later. An operator with no number is never reached — set one under
            Settings → Access.
          </p>
        </div>

        <Table>
          <TableBody>
            {(onCall ?? []).map((person) => (
              <TableRow key={person.id}>
                <TableCell>{person.full_name ?? person.email}</TableCell>
                <TableCell className="font-mono text-sm">
                  {person.phone_e164 ?? (
                    <span className="text-tomato-11">no number</span>
                  )}
                </TableCell>
                <TableCell className="w-10 text-right">
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-7 w-7"
                    aria-label="Remove from on call"
                    onClick={() =>
                      removeOnCall.mutate({
                        groupId,
                        operatorId: person.operator_id,
                      })
                    }
                  >
                    <XIcon className="w-4 h-4" />
                  </Button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>

        {(onCall?.length ?? 0) === 0 && (
          <p className="text-sm text-tomato-11">
            Nobody is on call. Alerts will still be raised and shown here, but
            no one will be contacted.
          </p>
        )}

        <div className="w-72">
          <Combobox
            options={candidates}
            value=""
            placeholder="Add someone to the on-call list…"
            searchPlaceholder="Search operators…"
            onValueChange={(v) => {
              if (!v) return;
              addOnCall.mutate({
                groupId,
                operatorId: Number(v) as OperatorId,
              });
            }}
          />
        </div>
      </section>

      <section className="space-y-2">
        <div>
          <h2 className="text-base font-semibold">Silence</h2>
          <p className="text-sm text-muted-foreground max-w-3xl">
            Stops the phone, not the watching. Alerts are still raised, still
            shown on this page, and still clear on their own — use this when you
            are about to do something that will trip them, like opening a vessel
            to sample.
          </p>
        </div>

        <div className="flex items-center gap-2">
          <SimpleSelect
            options={SNOOZE_CHOICES}
            value={snoozeMinutes}
            triggerClassName="w-40"
            onChange={(v) => setSnoozeMinutes(Number(v))}
          />
          <Button
            variant="ghost"
            size="sm"
            disabled={snooze.isPending}
            onClick={() =>
              snooze.mutate({
                groupId,
                fermentationId: null,
                groupAlertId: null,
                minutes: snoozeMinutes,
                reason: null,
              })
            }
          >
            <BellOffIcon className="w-4 h-4 mr-1" />
            Silence everything
          </Button>
        </div>

        {(snoozes?.length ?? 0) > 0 && (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Silenced</TableHead>
                <TableHead>Until</TableHead>
                <TableHead className="w-10" />
              </TableRow>
            </TableHeader>
            <TableBody>
              {(snoozes ?? []).map((row) => (
                <TableRow key={row.id}>
                  <TableCell>
                    {row.group_alert_id === null
                      ? "Every alert"
                      : `Alert #${row.group_alert_id}`}
                    {row.fermentation_id !== null &&
                      ` · tank #${row.fermentation_id}`}
                  </TableCell>
                  <TableCell className="text-sm">
                    {new Date(row.until).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-7 w-7"
                      aria-label="Cancel silence"
                      onClick={() => cancelSnooze.mutate(row.id)}
                    >
                      <XIcon className="w-4 h-4" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>

      <section className="space-y-2">
        <h2 className="text-base font-semibold">History</h2>
        {(history?.length ?? 0) === 0 ? (
          <p className="text-sm text-muted-foreground">
            Nothing has been raised on this run.
          </p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Raised</TableHead>
                <TableHead>Alert</TableHead>
                <TableHead>Tank</TableHead>
                <TableHead>What happened</TableHead>
                <TableHead>Outcome</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {(history ?? []).map((alert) => (
                <TableRow key={alert.id}>
                  <TableCell className="text-sm whitespace-nowrap">
                    {new Date(alert.raised_at).toLocaleString()}
                  </TableCell>
                  <TableCell className="text-sm">
                    {ALERT_KIND_LABELS[alert.kind]}
                  </TableCell>
                  <TableCell className="font-mono text-sm">
                    {alert.fermentation_name ?? "—"}
                  </TableCell>
                  <TableCell className="text-sm">{alert.detail}</TableCell>
                  <TableCell className="text-sm text-muted-foreground">
                    <Outcome alert={alert} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </section>
    </div>
  );
}

/** How an alert ended, and whether anyone was reached about it. */
function Outcome({
  alert,
}: {
  alert: {
    state: FermentationAlertStateEnum;
    cleared_at: Date | string | null;
    self_cleared: boolean;
    acked_by_name: string | null;
    voice_sent_at: Date | string | null;
  };
}) {
  if (alert.state !== FermentationAlertStateEnum.CLEARED) {
    return (
      <span className="flex items-center gap-1">
        {alert.state === FermentationAlertStateEnum.ACKED
          ? `Acknowledged by ${alert.acked_by_name ?? "someone"}`
          : "Open"}
        {alert.voice_sent_at && <PhoneIcon className="w-3 h-3" />}
      </span>
    );
  }
  return (
    <span>
      {alert.self_cleared
        ? "Recovered on its own"
        : `Closed by ${alert.acked_by_name ?? "someone"}`}
    </span>
  );
}
